import { atom, selector, useRecoilState, useRecoilValue } from 'recoil';

import { useCustomIntegrations } from '/hooks/useCustomIntegrations';
import { useScreens } from '/hooks/useScreens';

import { isMobileState } from './core';

/**
 * This file contains UI state pertaining to layout. E.g.: are we on a mobile device or not,
 * which view mode is preferred.
 */

const mobileTrayState = atom<boolean>({
  key: 'mobile-tray',
  default: true,
});
/**
 * Returns whether the mobile tray should be shown and a setter to change it.
 */
export const useMobileTray = () => useRecoilState(mobileTrayState);

const showMobileMoreMenuState = atom<boolean>({
  key: 'show-mobile-more-menu-state',
  default: false,
});
/**
 * Returns a flag indicating whether to show the More menu in mobile view.
 */
export const useShowMobileMoreMenu = () =>
  useRecoilState(showMobileMoreMenuState);

const showMobilePageMenuState = atom<boolean>({
  key: 'show-mobile-page-menu-state',
  default: false,
});
/**
 * Returns a flag indicating whether to show the page menu in mobile view.
 */
export const useShowMobilePageMenu = () =>
  useRecoilState(showMobilePageMenuState);

const pinnedIdState = atom<string | null>({
  key: 'pinned-id',
  default: null,
});
/**
 * Returns the currently pinned session or screen id and a setter to change it.
 */
export const usePinnedId = () => useRecoilState(pinnedIdState);

export type PreferredViewMode = 'grid' | 'speaker';

const preferredViewModeState = atom<PreferredViewMode>({
  key: 'preferred-view-mode',
  default: new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve('speaker');
      return;
    }
    const search = new URLSearchParams(window.location.search);
    if (search.get('v') === 'grid') {
      resolve('grid');
      return;
    }
    resolve('speaker');
    return;
  }),
});
/**
 * Returns the currently preferred view mode and a setter to change it.
 */
export const usePreferredViewMode = () =>
  useRecoilState(preferredViewModeState);

type ViewMode = PreferredViewMode | 'mobile';

const viewModeSelector = selector<ViewMode>({
  key: 'view-mode',
  get: ({ get }) => {
    const isMobile = get(isMobileState);
    if (isMobile) return 'mobile';
    const pinnedId = get(pinnedIdState);
    if (pinnedId) return 'speaker';
    const preferredViewMode = get(preferredViewModeState);
    return preferredViewMode;
  },
});
/**
 * Returns the current view mode: 'grid', 'speaker' or 'mobile'.
 * In case a participant or screen is pinned or a screen is shared, it will default to 'speaker'.
 */
export const useViewMode = () => {
  const screens = useScreens();
  const viewMode = useRecoilValue(viewModeSelector);
  const [pinnedId] = usePinnedId();
  const { runningMainIntegrations } = useCustomIntegrations();
  if (
    (screens.length + runningMainIntegrations.length > 0 &&
      viewMode !== 'mobile') ||
    pinnedId
  )
    return 'speaker';
  return viewMode;
};
